import moment from 'moment';
import 'moment/locale/pt-br';

import monetize from '@sdk/helpers/formatters/monetize';

import { PHONE_FORMAT } from '@cordel/helpers/form/validators/phone';
import { CNPJ_FORMAT } from '@cordel/helpers/form/validators/cnpj';
import { CELLPHONE_FORMAT } from '@cordel/helpers/form/validators/cellphone';
import { DATE_FORMAT } from '@cordel/helpers/form/validators/date';
import { CPF_FORMAT } from '@cordel/helpers/form/validators/cpf';
import { ZIP_FORMAT } from '@cordel/helpers/form/validators/zip';

import cachedDomains from '@/helpers/apis/domains';

import { percentize } from '@/helpers/filters';

import {
  boolean,
  file,
  input,
  select,
} from '@/_CRUDL/helpers/form/fields';

import { customerTypes } from '@/helpers/options/boolean';

import customerMap, {
  monthlyInvoicingMap,
} from '@/views/Customers/Form/maps';

export const sectionsIDs = {
  customer_personal: 'customerPersonal',
  customer_financial: 'customerFinancial',
  customer_birth: 'customerBirth',
  customer_document: 'customerDocument',
  customer_CNH: 'customerCNH',
  customer_phones: 'customerPhones',
  customer_emails: 'customerEmails',
  customer_references: 'customerReferences',
  customer_partners: 'customerPartners',
  customer_partners_spouse: 'customerPartnersSpouse',
  customer_family: 'customerFamily',
  customer_spouse: 'customerSpouse',
  customer_address: 'customerAddress',
  customer_PREVIOUS_ADDRESS: 'customerPreviousAddress',
  customer_alternative_address: 'customerAlternativeAddress',
  customer_job: 'customerJob',
  customer_professional: 'customerProfessional',
  customer_professional_address: 'customerProfessionalAddress',
  customer_professional_income: 'customerProfessionalIncome',
  customer_previous_professional: 'customerPreviousProfessional',
  customer_accountant: 'customerAccountant',
  customer_bank: 'customerBank',
  customer_history: 'customerHistory',
  customer_obs: 'customerObs',
};

export const pfORpjFields = {
  [sectionsIDs.customer_personal]: {
    subtitle: {
      pj: 'Informações da empresa',
      pf: 'Informações do cliente',
    },
    labels: {
      cpf_cnpj: {
        pf: 'CPF',
        pj: 'CNPJ',
      },
      name: {
        pf: 'Nome',
        pj: 'Razão social',
      },
      nickname: {
        pf: 'Apelido',
        pj: 'Nome',
      },
      birthdate: {
        pf: 'Data de nascimento',
        pj: 'Data de fundação',
      },
    },
    validators: {
      cpf_cnpj: {
        pf: 'cpf',
        pj: 'cnpj',
      },
    },
    placeholders: {
      cpf_cnpj: {
        pf: '000.000.000-00',
        pj: '00.000.000/0000-00',
      },
      name: {
        pf: 'Nome do cliente',
        pj: 'Nome da empresa',
      },
      nickname: {
        pf: 'Apelido do cliente',
        pj: 'Nome fantasia',
      },
    },
  },
  [sectionsIDs.customer_address]: {
    subtitle: {
      pf: 'Lugar de moradia atual',
      pj: 'Localização atual',
    },
    labels: {
      build_type_id: {
        pf: 'Tipo de residência',
        pj: 'Tipo de endereço',
      },
    },
  },
  [sectionsIDs.customer_PREVIOUS_ADDRESS]: {
    subtitle: {
      pf: 'Lugar de moradia anterior',
      pj: 'Localização anterior',
    },
    labels: {
      build_type_id: {
        pf: 'Tipo de residência',
        pj: 'Tipo de endereço',
      },
    },
  },
};

export const customerType = (config = {}, isPj) => (isPj ? config.pj : config.pf);

function addressFields(map = {}, full = false) {
  const residence = [{
    id: map.INTERNAL_residence_separator,
    separator: true,
  }, {
    ...select,
    id: map.build_type_id,
    label: 'Tipo de residência',
    placeholder: 'Selecione um tipo de residência',
    size: 2,
  }, {
    ...input,
    id: map.rent_value_cents,
    label: 'Aluguel / prestação',
    placeholder: 'R$ 0,00',
    mask: {
      type: 'monetize',
    },
    validations: [{
      type: 'blank',
    }, {
      type: 'greaterThan',
      count: 0,
      formatter: monetize,
    }],
  }, {
    ...input,
    id: map.set_time_year,
    label: 'Neste endereço desde',
    placeholder: 'dd/mm/aaaa',
    mask: {
      format: DATE_FORMAT,
      type: 'patternize',
    },
    validations: [{
      type: 'blank',
    }, {
      type: 'date',
    }],
  }];

  const address = [{
    ...input,
    id: map.zip_code,
    label: 'CEP',
    placeholder: '00000-000',
    mask: {
      format: ZIP_FORMAT,
      type: 'patternize',
    },
    validations: [{
      type: 'blank',
    }, {
      type: 'zip',
    }],
  }, {
    ...select,
    id: map.state_id,
    label: 'Estado',
    placeholder: 'Estado do cliente',
  }, {
    ...input,
    id: map.city,
    label: 'Cidade',
    placeholder: 'Cidade do cliente',
  }, {
    ...input,
    id: map.neighborhood,
    label: 'Bairro',
    placeholder: 'Bairro do cliente',
  }, {
    ...input,
    id: map.street,
    label: 'Logradouro',
    placeholder: 'Rua do cliente',
    size: 2,
  }, {
    ...input,
    id: map.number,
    label: 'Número',
    placeholder: '123',
  }, {
    ...input,
    id: map.complement,
    label: 'Complemento',
    placeholder: 'Bloco X, Apto 123',
    validations: [{
      type: 'invalid',
    }],
  }];

  if (full) {
    return address.concat(residence);
  }

  return address;
}

function monthlyInvoicingFields() {
  return customerMap.monthly_billings.map((id) => {
    const date = moment(1, 'DD').subtract(
      id.replace(`${monthlyInvoicingMap.billing_cents}-`, ''),
      'months',
    );

    return {
      ...input,
      id,
      data: {
        month: date.format('M'),
        year: date.format('Y'),
      },
      label: date.format('MMMM, Y'),
      placeholder: 'R$ 0,00',
      mask: {
        type: 'monetize',
      },
      validations: [{
        type: 'blank',
      }, {
        type: 'greaterThan',
        count: 0,
        equal: true,
        formatter: monetize,
      }],
    };
  });
}

function addressesFields(pj = false) {
  return [{
    id: sectionsIDs.customer_address,
    title: 'Endereço atual',
    subtitle: customerType(pfORpjFields[sectionsIDs.customer_address].subtitle, pj),
    items: addressFields(customerMap.address, true)
      .map((config) => {
        if (config.id === customerMap.address.build_type_id) {
          config.label = customerType(
            pfORpjFields[sectionsIDs.customer_address].labels.build_type_id,
            pj,
          );
        }

        return config;
      })
      .concat([{
        id: customerMap.address.INTERNAL_address_file_separator,
        separator: true,
      }, {
        ...file,
        id: customerMap.address.address_file,
        label: 'Comprovante de Endereço',
        meta: {
          name: cachedDomains.person_attachment_types.ADDRESS.name,
          type: cachedDomains.person_attachment_types.ADDRESS.type,
        },
        validations: [],
      }]),
  }, {
    id: sectionsIDs.customer_PREVIOUS_ADDRESS,
    title: 'Endereço anterior',
    subtitle: customerType(pfORpjFields[sectionsIDs.customer_PREVIOUS_ADDRESS].subtitle, pj),
    items: [{
      ...boolean,
      id: customerMap.addresses[0].INTERNAL_present,
      label: 'Incluir?',
      placeholder: 'Incluir endereço anterior?',
      validations: [],
    }, {
      id: customerMap.addresses[0].INTERNAL_address_separator,
      separator: true,
    }].concat(addressFields(customerMap.addresses[0], true).map((config) => {
      if (config.id === customerMap.addresses[0].build_type_id) {
        config.label = customerType(
          pfORpjFields[sectionsIDs.customer_PREVIOUS_ADDRESS].labels.build_type_id,
          pj,
        );
      }

      return config;
    })),
  }, {
    id: sectionsIDs.customer_alternative_address,
    title: 'Endereço alternativo',
    subtitle: 'Lugar para correspondências alternativo',
    items: [{
      ...boolean,
      id: customerMap.addresses[1].INTERNAL_present,
      label: 'Incluir?',
      placeholder: 'Incluir endereço alternativo?',
      validations: [],
    }, {
      id: customerMap.addresses[1].INTERNAL_address_separator,
      separator: true,
    }].concat(addressFields(customerMap.addresses[1], false)),
  }];
}

function personalFields(pj = false) {
  const customerTypeFields = [{
    ...boolean,
    id: customerMap.INTERNAL_customer_type,
    label: 'Tipo de cliente',
    placeholder: 'Tipo de cliente',
    validations: [{
      type: 'blank',
    }],
    skip: true,
    size: 3,
    options: customerTypes,
  }, {
    id: customerMap.INTERNAL_customer_type_separator,
    separator: true,
  }];

  const pjFields = [{
    ...input,
    id: customerMap.joint_stock_cents,
    label: 'Capital social',
    placeholder: 'Capital social',
    mask: {
      type: 'monetize',
    },
    validations: [{
      type: 'blank',
    }, {
      type: 'greaterThan',
      count: 0,
    }],
  }, {
    id: customerMap.INTERNAL_joint_separator,
    separator: true,
  }, {
    ...input,
    id: customerMap.ie,
    label: 'Inscrição estadual',
    placeholder: 'Número da inscrição estadual',
    size: 3,
    validations: [],
  }, {
    ...input,
    id: customerMap.im,
    label: 'Inscrição municipal',
    placeholder: 'Número da inscrição municipal',
    size: 3,
    validations: [],
  }, {
    ...select,
    id: customerMap.company_activity_id,
    label: 'Ramo de atividade',
    placeholder: 'Selecione um ramo de atividade',
    size: 3,
  }, {
    id: customerMap.INTERNAL_company_files_separator,
    separator: true,
  }, {
    ...file,
    id: customerMap.cnpj_file,
    label: 'Comprovante de CNPJ',
    meta: {
      name: cachedDomains.attachment_types.CNPJ.name,
      type: cachedDomains.attachment_types.CNPJ.type,
    },
    validations: [],
    size: 2,
  }, {
    ...file,
    id: customerMap.cs_file,
    label: 'Contrato social',
    meta: {
      name: cachedDomains.attachment_types.CS.name,
      type: cachedDomains.attachment_types.CS.type,
    },
    validations: [],
    size: 2,
  }];

  const pfFields = [{
    ...select,
    id: customerMap.genre_id,
    label: 'Gênero',
    placeholder: 'Selecione um gênero',
    skip: pj,
  }, {
    ...select,
    id: customerMap.education_id,
    label: 'Escolaridade',
    placeholder: 'Selecione uma escolaridade',
    skip: pj,
    size: 2,
  }];

  const items = [{
    ...input,
    id: customerMap.cpf_cnpj,
    label: customerType(pfORpjFields[sectionsIDs.customer_personal]
      .labels
      .cpf_cnpj, pj),
    placeholder: customerType(pfORpjFields[sectionsIDs.customer_personal]
      .placeholders
      .cpf_cnpj, pj),
    disabled: true,
    mask: {
      format: pj ? CNPJ_FORMAT : CPF_FORMAT,
      type: 'patternize',
    },
    validations: [{
      type: 'blank',
    }, {
      type: customerType(pfORpjFields[sectionsIDs.customer_personal]
        .validators
        .cpf_cnpj, pj),
    }],
  }, {
    ...input,
    id: customerMap.name,
    label: customerType(pfORpjFields[sectionsIDs.customer_personal].labels.name, pj),
    placeholder: customerType(pfORpjFields[sectionsIDs.customer_personal].placeholders.name, pj),
    size: 2,
  }];

  if (!pj) {
    items.push({
      ...input,
      id: customerMap.social_name,
      label: 'Nome social',
      placeholder: 'Nome social completo',
      size: 2,
    });
  }

  items.push({
    ...input,
    id: customerMap.nickname,
    label: customerType(pfORpjFields[sectionsIDs.customer_personal].labels.nickname, pj),
    placeholder: customerType(pfORpjFields[sectionsIDs.customer_personal]
      .placeholders
      .nickname, pj),
    validations: [],
  }, {
    ...input,
    id: customerMap.born_at,
    label: customerType(pfORpjFields[sectionsIDs.customer_personal].labels.birthdate, pj),
    placeholder: 'dd/mm/aaaa',
    mask: {
      format: DATE_FORMAT,
      type: 'patternize',
    },
    validations: [{
      type: 'blank',
    }, {
      type: 'date',
    }, {
      type: 'requiredLegalAge',
      age: '+18',
    }],
  });

  const fields = {
    id: sectionsIDs.customer_personal,
    title: 'Identificação',
    subtitle: customerType(pfORpjFields[sectionsIDs.customer_personal].subtitle, pj),
    slots: true,
    items,
  };

  if (pj) {
    return [{
      ...fields,
      items: [
        ...customerTypeFields,
        ...fields.items.concat(pjFields),
      ],
    }];
  }

  return [{
    ...fields,
    items: [
      ...customerTypeFields, {
        ...file,
        id: customerMap.cnh_ocr_file,
        label: 'Carteira de Motorista',
        placeholder: 'Inicie este cadastro enviando uma imagem da carteira de motorista (aberta) no campo abaixo para aumentar suas chances de aprovação e ganhar tempo preenchendo campos deste formulário automaticamente.',
        meta: {
          name: cachedDomains.person_attachment_types.CNH.name,
          type: cachedDomains.person_attachment_types.CNH.type,
          accept: 'image/*',
        },
        validations: [],
        size: 4,
        skip: true,
      }, {
        id: 'leadCNHFileSeparator',
        separator: true,
      },
      ...fields.items.concat(pfFields)],
  }];
}

function historyFields(pj = false) {
  const section = {
    id: sectionsIDs.customer_history,
    title: 'Histórico',
    subtitle: 'Informações extras',
    items: [{
      ...boolean,
      id: customerMap.has_made_funding,
      label: 'Já financiou algum veículo?',
      placeholder: 'Já financiou veículo anteriormente?',
    }, {
      ...select,
      id: customerMap.previous_funding_bank_id,
      label: 'Com qual banco?',
      placeholder: 'Selecione o banco anterior',
      skip: true,
      size: 2,
    }],
  };

  if (!pj) {
    section.items.push({
      id: 'historySeparator',
      separator: true,
    }, {
      ...boolean,
      id: customerMap.have_credit_card,
      label: 'Possui cartões?',
      placeholder: 'Possui cartões de crédito?',
    }, {
      ...input,
      id: customerMap.credit_cards,
      label: 'Quais cartões?',
      placeholder: 'Exemplo: Mastercard, Hipercard',
      size: 2,
    }, {
      id: customerMap.INTERNAL_property_separator,
      separator: true,
    }, {
      ...select,
      id: customerMap.property,
      label: 'Patrimônio',
      placeholder: 'Selecione um patrimônio',
      size: 2,
    }, {
      id: customerMap.INTERNAL_public_person_separator,
      separator: true,
    }, {
      ...boolean,
      id: customerMap.public_person,
      label: 'Pessoa publicamente exposta?',
      placeholder: 'O cliente é publicamente exposto?',
    });
  }

  return [section];
}

function bankFields(pj = false) {
  return [{
    id: sectionsIDs.customer_bank,
    title: 'Conta bancária',
    subtitle: 'Dados bancários',
    items: [{
      ...boolean,
      id: customerMap.have_bank_account,
      label: 'Conta bancária?',
      placeholder: 'Possui conta bancária?',
      skip: pj,
    }, {
      id: customerMap.bank_references[0].INTERNAL_bank_separator,
      separator: true,
      skip: true,
    }, {
      ...select,
      id: customerMap.bank_references[0].bank_id,
      label: 'Banco',
      placeholder: 'Selecione um banco',
      skip: !pj,
    }, {
      ...input,
      id: customerMap.bank_references[0].agency,
      label: 'Agência',
      placeholder: 'Agência da conta',
      skip: !pj,
      validations: [{
        type: 'blank',
      }, {
        type: 'tooLarge',
        count: 6,
      }],
    }, {
      ...input,
      id: customerMap.bank_references[0].account_number,
      label: 'Conta',
      placeholder: '0000-0',
      skip: !pj,
      validations: [{
        type: 'blank',
      }, {
        type: 'digit',
      }],
    }, {
      ...input,
      id: customerMap.bank_references[0].open_at,
      label: 'Data de abertura',
      placeholder: 'dd/mm/aaaa',
      skip: !pj,
      mask: {
        format: DATE_FORMAT,
        type: 'patternize',
      },
      validations: [{
        type: 'blank',
      }, {
        type: 'date',
      }],
    }, {
      ...boolean,
      id: customerMap.bank_references[0].overdraft,
      label: 'Cheque especial?',
      placeholder: 'Possui cheque especial?',
      skip: !pj,
    }],
  }];
}

function emailsFields() {
  return [{
    id: sectionsIDs.customer_emails,
    title: 'E-mail',
    subtitle: 'Dados de contato',
    items: [{
      ...input,
      id: customerMap.emails[0].address,
      label: 'E-mail',
      placeholder: 'email@exemplo.com',
      size: 2,
      validations: [{
        type: 'email',
      }, {
        type: 'blank',
      }],
    }, {
      ...boolean,
      id: customerMap.accept_boleto,
      label: 'Receber boleto?',
      placeholder: 'Receber boletos neste e-mail?',
    }],
  }];
}

function accountantFields(pj = false) {
  return [{
    id: sectionsIDs.customer_accountant,
    title: 'Contador',
    subtitle: 'Dados de contabilidade',
    items: [{
      ...input,
      id: customerMap.accountant.name,
      label: 'Nome',
      placeholder: 'Nome do contador',
      size: 2,
      skip: !pj,
    }, {
      ...input,
      id: customerMap.accountant.city,
      label: 'Cidade',
      placeholder: 'Cidade do contador',
      skip: !pj,
    }, {
      ...input,
      id: customerMap.accountant.phone,
      label: 'Telefone',
      placeholder: 'Número do contador',
      skip: !pj,
      mask: {
        format: PHONE_FORMAT,
        bigFormat: CELLPHONE_FORMAT,
        type: 'patternize',
      },
      validations: [{
        type: 'blank',
      }, {
        type: 'phone',
      }],
    }],
  }];
}

function phonesFields() {
  return [{
    id: sectionsIDs.customer_phones,
    title: 'Telefones',
    subtitle: 'Números de contato',
    items: [{
      ...input,
      id: customerMap.phones[0].number,
      label: 'Celular',
      placeholder: 'Telefone celular',
      mask: {
        format: CELLPHONE_FORMAT,
        type: 'patternize',
      },
      validations: [{
        type: 'blank',
      }, {
        type: 'cellphone',
      }],
    }, {
      ...input,
      id: customerMap.phones[0].phone_confirmation_id,
      skip: true,
      validations: [],
    }, {
      ...input,
      id: customerMap.phones[1].number,
      label: 'Alternativo',
      placeholder: 'Telefone alternativo',
      mask: {
        format: PHONE_FORMAT,
        bigFormat: CELLPHONE_FORMAT,
        type: 'patternize',
      },
      validations: [{
        type: 'blank',
      }, {
        type: 'phone',
      }],
    }, {
      ...input,
      id: customerMap.phones[1].phone_confirmation_id,
      skip: true,
      validations: [],
    }],
  }];
}

function obsFields() {
  return [{
    id: sectionsIDs.customer_obs,
    title: 'Observações internas',
    subtitle: 'Observações no cadastro do cliente',
    items: [{
      ...input,
      id: customerMap.note,
      label: 'Observação',
      placeholder: 'Escreva uma observação',
      validations: [],
      size: 4,
    }],
  }];
}

export function sectionsPJ() {
  return [
    ...personalFields(true), {
      id: sectionsIDs.customer_financial,
      title: 'Financeiro',
      subtitle: 'Informações de faturamento',
      items: [
        ...monthlyInvoicingFields(),
        {
          id: customerMap.INTERNAL_company_fat_file_separator,
          separator: true,
        }, {
          ...file,
          id: customerMap.fat_file,
          label: 'Comprovante de faturamento',
          meta: {
            name: cachedDomains.attachment_types.FAT.name,
            type: cachedDomains.attachment_types.FAT.type,
          },
          validations: [],
          size: 2,
        }],
    },

    ...phonesFields(),
    ...emailsFields(),

    {
      id: sectionsIDs.customer_partners,
      title: 'Sócio principal',
      subtitle: 'Informações do sócio principal',
      items: [{
        ...input,
        id: customerMap.partners[0].cpf_cnpj,
        label: 'CPF/CNPJ',
        placeholder: '000.000.000-00',
        mask: {
          format: CPF_FORMAT,
          bigFormat: CNPJ_FORMAT,
          type: 'patternize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'cpfOrCnpj',
        }],
      }, {
        ...input,
        id: customerMap.partners[0].name,
        label: 'Nome',
        placeholder: 'Nome do sócio',
        size: 2,
      }, {
        ...input,
        id: customerMap.partners[0].born_at,
        label: 'Data de nascimento',
        placeholder: 'dd/mm/aaaa',
        mask: {
          format: DATE_FORMAT,
          type: 'patternize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'date',
        }, {
          type: 'requiredLegalAge',
          age: '+18',
        }],
      }, {
        ...input,
        id: customerMap.partners[0].started_at,
        label: 'Sócio desde',
        placeholder: 'dd/mm/aaaa',
        mask: {
          format: DATE_FORMAT,
          type: 'patternize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'date',
        }],
      }, {
        ...input,
        id: customerMap.partners[0].quota,
        label: 'Participação',
        placeholder: '0%',
        mask: {
          type: 'monetize',
          prefix: '',
          suffix: '%',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'greaterThan',
          count: 0,
          formatter: percentize,
        }, {
          type: 'lessThan',
          count: 100 * 100,
          equal: true,
          formatter: percentize,
        }],
      }, {
        ...input,
        id: customerMap.partners[0].income_cents,
        label: 'Renda comprovada (PF)',
        placeholder: 'R$ 0,00',
        mask: {
          type: 'monetize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'greaterThan',
          count: 0,
          formatter: monetize,
        }],
      }, {
        ...select,
        id: customerMap.partners[0].role,
        label: 'Função',
        placeholder: 'Função do sócio',
      }, {
        ...select,
        id: customerMap.partners[0].marital_status_id,
        label: 'Estado civil',
        placeholder: 'Selecione um estado civil',
        size: 2,
      }],
    }, {
      id: sectionsIDs.customer_partners_spouse,
      title: 'Cônjuge do sócio principal',
      subtitle: 'Dados do cônjuge',
      items: [{
        ...input,
        id: customerMap.partners[0].spouse_name,
        label: 'Nome',
        placeholder: 'Nome do cônjuge',
        size: 2,
        skip: true,
      }, {
        ...input,
        id: customerMap.partners[0].spouse_cpf,
        label: 'CPF',
        placeholder: '000.000.000-00',
        skip: true,
        mask: {
          format: CPF_FORMAT,
          type: 'patternize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'cpf',
        }, {
          type: 'not-equal-to',
          field: 'cpf',
        }],
      }, {
        ...input,
        id: customerMap.partners[0].spouse_born_at,
        label: 'Data de nascimento',
        placeholder: 'dd/mm/aaaa',
        skip: true,
        mask: {
          format: DATE_FORMAT,
          type: 'patternize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'date',
        }],
      }],
    },

    ...addressesFields(true),
    ...accountantFields(true),
    ...bankFields(true),
    ...historyFields(true),
    ...obsFields(),
  ];
}

export function sectionsPF() {
  return [
    ...personalFields(),
    {
      id: sectionsIDs.customer_birth,
      title: 'Nacionalidade',
      subtitle: 'Informações de naturalidade',
      items: [{
        ...boolean,
        id: customerMap.nationality,
        label: 'Brasileiro?',
        placeholder: 'O cliente é brasileiro?',
      }, {
        ...select,
        id: customerMap.state_of_birth_id,
        label: 'Estado',
        placeholder: 'Estado de nascimento',
        skip: true,
        size: 2,
      }, {
        ...input,
        id: customerMap.place_of_birth,
        label: 'Cidade',
        placeholder: 'Cidade de nascimento',
        skip: true,
      }],
    }, {
      id: sectionsIDs.customer_document,
      title: 'Documento',
      subtitle: 'Identificação do cliente',
      items: [{
        ...select,
        id: customerMap.document_type,
        label: 'Tipo do documento',
        placeholder: 'Selecione um documento',
        include: ['RG', '1015', 'CTPS', 'RNE'],
        size: 2,
      }, {
        id: customerMap.INTERNAL_document_separator,
        separator: true,
      }, {
        ...input,
        id: customerMap.rg,
        label: 'Número',
        placeholder: 'Número do documento',
      }, {
        ...input,
        id: customerMap.rg_issuing,
        label: 'Órgão emissor',
        placeholder: 'Emissor do documento',
      }, {
        ...select,
        id: customerMap.rg_state_id,
        label: 'Estado',
        placeholder: 'Estado de emissão',
      }, {
        ...input,
        id: customerMap.rg_date,
        label: 'Data de emissão',
        placeholder: 'dd/mm/aaaa',
        mask: {
          format: DATE_FORMAT,
          type: 'patternize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'date',
        }],
      }, {
        id: customerMap.INTERNAL_document_file_separator,
        separator: true,
      }, {
        ...file,
        id: customerMap.rg_front_file,
        label: 'RG (frente)',
        meta: {
          name: cachedDomains.person_attachment_types.RG_FRONT.name,
          type: cachedDomains.person_attachment_types.RG_FRONT.type,
        },
        validations: [],
        skip: true,
      }, {
        ...file,
        id: customerMap.rg_back_file,
        label: 'RG (verso)',
        meta: {
          name: cachedDomains.person_attachment_types.RG_BACK.name,
          type: cachedDomains.person_attachment_types.RG_BACK.type,
        },
        validations: [],
        skip: true,
      }, {
        ...file,
        id: customerMap.ctps_file,
        label: 'Carteira de Trabalho',
        meta: {
          name: cachedDomains.person_attachment_types.CTPS.name,
          type: cachedDomains.person_attachment_types.CTPS.type,
        },
        validations: [],
        skip: true,
      }],
    }, {
      id: sectionsIDs.customer_CNH,
      title: 'Carteira de motorista',
      subtitle: 'Dados da carteira de habilitação',
      items: [{
        ...boolean,
        id: customerMap.has_cnh,
        label: 'Possui?',
        placeholder: 'Possui carteira de motorista?',
        validations: [],
      }, {
        ...input,
        id: customerMap.cnh,
        label: 'Número',
        placeholder: 'Número da carteira',
        skip: true,
      }, {
        ...select,
        id: customerMap.cnh_type_id,
        label: 'Categoria',
        placeholder: 'Categoria da carteira',
        skip: true,
        size: 2,
      }, {
        id: customerMap.INTERNAL_cnh_file_separator,
        separator: true,
      }, {
        ...file,
        id: customerMap.cnh_file,
        label: 'Carteira de Motorista',
        meta: {
          name: cachedDomains.person_attachment_types.CNH.name,
          type: cachedDomains.person_attachment_types.CNH.type,
        },
        validations: [],
        skip: true,
      }],
    },
    ...phonesFields(),
    ...emailsFields(),

    {
      id: sectionsIDs.customer_references,
      title: 'Contato de referência',
      subtitle: 'Por exemplo, pai, prima, amigo, irmã, etc.',
      items: [{
        ...input,
        id: customerMap.personal_references[0].name,
        label: 'Nome',
        placeholder: 'Nome da referência',
        size: 2,
      }, {
        ...select,
        id: customerMap.personal_references[0].relationship,
        label: 'Relação',
        placeholder: 'Relação com a referência',
        size: 2,
      }, {
        ...input,
        id: customerMap.personal_references[0].city,
        label: 'Cidade',
        placeholder: 'Cidade da referência',
        size: 2,
      }, {
        ...input,
        id: customerMap.personal_references[0].phone,
        label: 'Telefone',
        placeholder: 'Número da referência',
        mask: {
          format: PHONE_FORMAT,
          bigFormat: CELLPHONE_FORMAT,
          type: 'patternize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'phone',
        }],
      }],
    }, {
      id: sectionsIDs.customer_family,
      title: 'Filiação',
      subtitle: 'Parentesco',
      items: [{
        ...input,
        id: customerMap.mother,
        label: 'Nome da mãe',
        placeholder: 'Mãe do cliente',
        size: 2,
      }, {
        ...input,
        id: customerMap.father,
        label: 'Nome do pai',
        placeholder: 'Pai do cliente',
        size: 2,
        validations: [],
      }, {
        ...select,
        id: customerMap.marital_status_id,
        label: 'Estado civil',
        placeholder: 'Selecione um estado civil',
        size: 2,
      }],
    }, {
      id: sectionsIDs.customer_spouse,
      title: 'Cônjuge',
      subtitle: 'Dados do cônjuge',
      items: [{
        ...input,
        id: customerMap.spouse_name,
        label: 'Nome',
        placeholder: 'Nome do cônjuge',
        size: 2,
        skip: true,
      }, {
        ...input,
        id: customerMap.spouse_cpf,
        label: 'CPF',
        placeholder: '000.000.000-00',
        skip: true,
        mask: {
          format: CPF_FORMAT,
          type: 'patternize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'cpf',
        }, {
          type: 'not-equal-to',
          field: 'cpf',
        }],
      }, {
        ...input,
        id: customerMap.spouse_born_at,
        label: 'Data de nascimento',
        placeholder: 'dd/mm/aaaa',
        skip: true,
        mask: {
          format: DATE_FORMAT,
          type: 'patternize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'date',
        }],
      }, {
        ...input,
        id: customerMap.spouse_income_cents,
        label: 'Renda mensal',
        placeholder: 'R$ 0,00',
        mask: {
          type: 'monetize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'greaterThan',
          count: 0,
          formatter: monetize,
        }],
      }],
    },

    ...addressesFields(),

    {
      id: sectionsIDs.customer_job,
      title: 'Profissão',
      subtitle: 'Dados profissionais',
      items: [{
        ...select,
        id: customerMap.job_reference.professional_ocupation_id,
        label: 'Ocupação',
        placeholder: 'Selecione uma ocupação',
        size: 2,
      }, {
        ...boolean,
        id: customerMap.job_reference.INTERNAL_still_works,
        label: 'Ainda trabalha?',
        skip: true,
      }, {
        ...select,
        id: customerMap.job_reference.profession_id,
        label: 'Profissão',
        placeholder: 'Selecione uma profissão',
        size: 2,
      }, {
        ...input,
        id: customerMap.job_reference.department,
        label: 'Departamento',
        placeholder: 'Departamento em que trabalha',
        size: 2,
      }, {
        ...input,
        id: customerMap.job_reference.joined_at,
        label: 'Data de contratação',
        placeholder: 'dd/mm/aaaa',
        mask: {
          format: DATE_FORMAT,
          type: 'patternize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'date',
        }],
      }],
    }, {
      id: sectionsIDs.customer_professional,
      title: 'Emprego atual',
      subtitle: 'Dados do seu emprego atual',
      items: [{
        ...input,
        id: customerMap.job_reference.name,
        label: 'Empresa',
        placeholder: 'Nome da empresa',
        size: 2,
      }, {
        ...input,
        id: customerMap.job_reference.cnpj,
        label: 'CNPJ',
        placeholder: '00.000.000/0000-00',
        mask: {
          format: CNPJ_FORMAT,
          type: 'patternize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'cnpj',
        }],
      }, {
        ...select,
        id: customerMap.job_reference.company_activity_id,
        label: 'Ramo de atividade',
        placeholder: 'Selecione um ramo de atividade',
        size: 2,
      }, {
        ...input,
        id: customerMap.job_reference.phone,
        label: 'Telefone',
        placeholder: 'Número de telefone',
        mask: {
          format: PHONE_FORMAT,
          bigFormat: CELLPHONE_FORMAT,
          type: 'patternize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'phone',
        }],
      }],
    }, {
      id: sectionsIDs.customer_professional_address,
      title: 'Endereço do emprego atual',
      subtitle: 'Informações trabalhistas',
      items: [{
        ...boolean,
        button: true,
        id: customerMap.job_reference.INTERNAL_reply_customer_address,
        label: 'Replicar endereço residencial?',
        validations: [],
        skip: true,
      }, {
        id: customerMap.job_reference.INTERNAL_reply_customer_address_separator,
        separator: true,
        skip: true,
      }, ...addressFields(customerMap.job_reference.address)],
    }, {
      id: sectionsIDs.customer_professional_income,
      title: 'Renda',
      subtitle: 'Informações trabalhistas',
      items: [{
        ...input,
        id: customerMap.job_reference.detail,
        label: 'Benefício INSS',
        placeholder: 'Número do benefício',
        skip: true,
      }, {
        ...input,
        id: customerMap.job_reference.income_cents,
        label: 'Renda mensal',
        placeholder: 'R$ 0,00',
        mask: {
          type: 'monetize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'greaterThan',
          count: 0,
          formatter: monetize,
        }],
      }, {
        ...input,
        id: customerMap.job_reference.another_income_cents,
        label: 'Renda extra?',
        placeholder: 'R$ 0,00',
        mask: {
          type: 'monetize',
        },
        validations: [],
      }, {
        ...select,
        id: customerMap.job_reference.another_income_type_id,
        label: 'Origem de extras',
        placeholder: 'Origem das rendas extras',
        size: 2,
      }, {
        id: customerMap.job_reference.INTERNAL_job_file_separator,
        separator: true,
      }, {
        ...file,
        id: customerMap.job_reference.job_file,
        label: 'Comprovante de Renda',
        meta: {
          name: cachedDomains.person_attachment_types.INCOME.name,
          type: cachedDomains.person_attachment_types.INCOME.type,
        },
        validations: [],
      }],
    }, {
      id: sectionsIDs.customer_previous_professional,
      title: 'Emprego anterior',
      subtitle: 'Dados do seu emprego anterior',
      items: [{
        ...boolean,
        id: customerMap.job_reference.first_job,
        label: 'Incluir?',
        placeholder: 'Incluir emprego anterior?',
        options: [{
          label: 'Sim',
          value: 'false',
        }, {
          label: 'Não',
          value: 'true',
        }],
      }, {
        id: 'firstJobSeparator',
        separator: true,
      }, {
        ...input,
        id: customerMap.job_reference.previous_work,
        label: 'Empresa',
        placeholder: 'Nome da empresa anterior',
        skip: true,
        size: 2,
      }, {
        ...input,
        id: customerMap.job_reference.previous_work_start_at,
        skip: true,
        label: 'Data de contratação',
        placeholder: 'dd/mm/aaaa',
        mask: {
          format: DATE_FORMAT,
          type: 'patternize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'date',
        }],
      }, {
        ...input,
        id: customerMap.job_reference.previous_work_end_at,
        skip: true,
        label: 'Data de demissão',
        placeholder: 'dd/mm/aaaa',
        mask: {
          format: DATE_FORMAT,
          type: 'patternize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'date',
        }],
      }, {
        ...input,
        id: customerMap.job_reference.previous_job_phone,
        skip: true,
        label: 'Telefone',
        placeholder: 'Número de telefone',
        mask: {
          format: PHONE_FORMAT,
          bigFormat: CELLPHONE_FORMAT,
          type: 'patternize',
        },
        validations: [{
          type: 'blank',
        }, {
          type: 'phone',
        }],
      }],
    },

    ...accountantFields(),
    ...bankFields(),
    ...historyFields(),
    ...obsFields(),
  ];
}

export default sectionsPF;
